import { financeiroAPI } from '../../lib/api';
import { toast } from 'sonner';

const fmtMoney = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const fmtDate = (d) => d ? new Date(d + (d.length === 10 ? 'T12:00:00' : '')).toLocaleDateString('pt-BR') : '—';

/**
 * Gera e abre janela com o recibo formatado para impressão / PDF.
 * Inclui logo da empresa, breakdown de subserviços e totalizadores.
 */
export async function imprimirRecibo(svc, parcela = null, recebedorNome = '') {
  try {
    const res = await financeiroAPI.recibo(svc.id, parcela?.id);
    const r = res.data;
    const empresa = r.empresa || {};
    const cliente = r.cliente || {};
    const servico = r.servico || {};
    const itens = servico.itens || [];

    // Nome que assina o recibo: usa o que o usuário digitou; fallback para proprietário/empresa
    const nomeRecebedor = (recebedorNome || '').trim() || empresa.proprietario || empresa.nome || '';

    const logoBlock = empresa.logo_url
      ? `<img src="${empresa.logo_url}" alt="logo" class="logo" />`
      : `<div class="logo-placeholder">${(empresa.nome || 'A').charAt(0).toUpperCase()}</div>`;

    const itensRows = itens.length === 0 ? '' : `
      <tr class="itens-header"><td colspan="2">Subserviços</td></tr>
      ${itens.map(it => `
        <tr class="item-row">
          <td>${escapeHtml(it.descricao || '—')}</td>
          <td class="right">${fmtMoney(it.valor)}</td>
        </tr>
      `).join('')}
    `;

    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="utf-8">
<title>Recibo · ${escapeHtml(servico.titulo || '')}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  @page { size: A4; margin: 10mm; }
  html, body { font-size: 12px; }
  body {
    font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    background: #f5f7fa;
    color: #1e293b;
    padding: 12px;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .recibo {
    max-width: 190mm;
    margin: 0 auto;
    background: #fff;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 6px 20px rgba(0,0,0,0.08);
    position: relative;
  }
  /* Header com gradient */
  .header {
    background: linear-gradient(135deg, #059669 0%, #0891b2 100%);
    color: white;
    padding: 14px 20px;
    display: flex;
    align-items: center;
    gap: 14px;
    position: relative;
    overflow: hidden;
  }
  .header::after {
    content: '';
    position: absolute;
    right: -40px;
    top: -40px;
    width: 140px;
    height: 140px;
    background: rgba(255,255,255,0.08);
    border-radius: 50%;
  }
  .logo, .logo-placeholder {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    background: white;
    flex-shrink: 0;
    object-fit: contain;
    padding: 4px;
    z-index: 2;
    position: relative;
  }
  .logo-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    font-weight: 800;
    color: #059669;
    background: white;
  }
  .header-info { z-index: 2; position: relative; flex: 1; }
  .header h1 { font-size: 17px; margin-bottom: 2px; letter-spacing: -0.2px; }
  .header .subtitle { font-size: 11px; opacity: 0.9; }
  .header .badge {
    display: inline-block;
    background: rgba(255,255,255,0.2);
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    margin-top: 4px;
  }

  /* Body */
  .body { padding: 14px 20px; }
  .section { margin-bottom: 12px; }
  .section-title {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #64748b;
    margin-bottom: 5px;
    padding-bottom: 3px;
    border-bottom: 1px solid #e2e8f0;
  }
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 16px; }
  .field-label {
    font-size: 9px;
    color: #64748b;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.4px;
  }
  .field-value { font-size: 12px; color: #1e293b; font-weight: 500; margin-top: 1px; }

  /* Tabela de valores */
  .valores-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 4px;
    background: #fafafa;
    border-radius: 8px;
    overflow: hidden;
  }
  .valores-table td {
    padding: 6px 10px;
    border-bottom: 1px solid #f1f5f9;
    font-size: 12px;
  }
  .valores-table .right { text-align: right; font-weight: 500; font-variant-numeric: tabular-nums; }
  .valores-table .itens-header td {
    background: #ecfeff;
    color: #0e7490;
    font-size: 9px;
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: 0.8px;
    padding: 5px 10px;
  }
  .valores-table .item-row td { padding-left: 18px; color: #475569; font-size: 11px; }
  .valores-table .principal-row td { background: #f0fdf4; }
  .valores-table .subtotal-row td {
    background: #f0f9ff;
    color: #0284c7;
    font-weight: 600;
    font-size: 11px;
    border-top: 1px solid #bae6fd;
  }
  .valores-table .total-row td {
    background: linear-gradient(90deg, #ecfdf5 0%, #ecfeff 100%);
    font-size: 14px;
    font-weight: 700;
    color: #059669;
    border-top: 2px solid #059669;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  /* Box do valor pago */
  .recibo-amount {
    text-align: center;
    background: linear-gradient(135deg, #ecfdf5 0%, #ecfeff 100%);
    border: 2px dashed #059669;
    border-radius: 10px;
    padding: 12px;
    margin: 12px 0;
  }
  .recibo-amount .label-rec {
    font-size: 9px;
    text-transform: uppercase;
    color: #047857;
    font-weight: 700;
    letter-spacing: 1.2px;
    margin-bottom: 2px;
  }
  .recibo-amount .amount {
    font-size: 26px;
    font-weight: 800;
    color: #059669;
    letter-spacing: -0.5px;
    line-height: 1;
  }
  .recibo-amount .context { font-size: 10px; color: #475569; margin-top: 4px; }

  .declaration {
    background: #fafafa;
    padding: 10px 12px;
    border-left: 3px solid #059669;
    border-radius: 4px;
    font-size: 11px;
    line-height: 1.5;
    color: #334155;
  }

  /* Assinatura */
  .signature {
    margin-top: 28px;
    text-align: center;
  }
  .signature-line {
    width: 220px;
    border-top: 1px solid #1e293b;
    margin: 0 auto 4px;
  }
  .signature-name { font-size: 11px; font-weight: 600; color: #1e293b; }
  .signature-role { font-size: 9px; color: #64748b; }

  /* Footer */
  .footer {
    background: #f8fafc;
    padding: 8px 20px;
    border-top: 1px solid #e2e8f0;
    text-align: center;
    font-size: 9px;
    color: #94a3b8;
    letter-spacing: 0.3px;
  }

  @media print {
    body { background: white; padding: 0; }
    .recibo { box-shadow: none; border-radius: 0; max-width: 100%; }
    .no-print { display: none; }
  }
  .print-btn {
    position: fixed;
    top: 12px;
    right: 12px;
    padding: 8px 14px;
    background: #059669;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 600;
    font-size: 12px;
    box-shadow: 0 4px 14px rgba(5,150,105,0.4);
  }
  .print-btn:hover { background: #047857; }
</style>
</head>
<body>
  <button class="print-btn no-print" onclick="window.print()">🖨️ Imprimir / Salvar PDF</button>

  <div class="recibo">
    <!-- HEADER -->
    <div class="header">
      ${logoBlock}
      <div class="header-info">
        <h1>${escapeHtml(empresa.nome || 'AgroLink')}</h1>
        <div class="subtitle">${escapeHtml(empresa.proprietario || '')}</div>
        <span class="badge">Recibo de Pagamento</span>
      </div>
    </div>

    <!-- BODY -->
    <div class="body">
      <!-- DADOS DO CLIENTE -->
      <div class="section">
        <div class="section-title">Dados do Cliente</div>
        <div class="grid-2">
          <div>
            <div class="field-label">Nome</div>
            <div class="field-value">${escapeHtml(cliente.nome || '—')}</div>
          </div>
          <div>
            <div class="field-label">CPF / CNPJ</div>
            <div class="field-value">${escapeHtml(cliente.cpf || '—')}</div>
          </div>
          ${cliente.telefone ? `<div><div class="field-label">Telefone</div><div class="field-value">${escapeHtml(cliente.telefone)}</div></div>` : ''}
          ${cliente.email ? `<div><div class="field-label">E-mail</div><div class="field-value">${escapeHtml(cliente.email)}</div></div>` : ''}
          ${cliente.endereco ? `<div style="grid-column: 1 / -1;"><div class="field-label">Endereço</div><div class="field-value">${escapeHtml(cliente.endereco)}</div></div>` : ''}
        </div>
      </div>

      <!-- DADOS DO SERVIÇO -->
      <div class="section">
        <div class="section-title">Serviço Prestado</div>
        <div class="grid-2">
          <div style="grid-column: 1 / -1;">
            <div class="field-label">Título</div>
            <div class="field-value" style="font-size: 16px; font-weight: 600;">${escapeHtml(servico.titulo || '')}</div>
          </div>
          ${servico.tipo_servico_nome ? `<div><div class="field-label">Tipo</div><div class="field-value">${escapeHtml(servico.tipo_servico_nome)}</div></div>` : ''}
          ${servico.categoria_nome ? `<div><div class="field-label">Categoria</div><div class="field-value">${escapeHtml(servico.categoria_nome)}</div></div>` : ''}
          ${servico.data_inicio ? `<div><div class="field-label">Início</div><div class="field-value">${fmtDate(servico.data_inicio)}</div></div>` : ''}
          ${servico.data_conclusao ? `<div><div class="field-label">Conclusão</div><div class="field-value">${fmtDate(servico.data_conclusao)}</div></div>` : ''}
          ${servico.forma_pagamento_nome ? `<div style="grid-column: 1 / -1;"><div class="field-label">Forma de Pagamento</div><div class="field-value">${escapeHtml(servico.forma_pagamento_nome)}</div></div>` : ''}
          ${servico.descricao ? `<div style="grid-column: 1 / -1;"><div class="field-label">Descrição</div><div class="field-value" style="font-weight: 400; line-height: 1.5; color: #475569;">${escapeHtml(servico.descricao)}</div></div>` : ''}
        </div>
      </div>

      <!-- DETALHAMENTO -->
      <div class="section">
        <div class="section-title">Detalhamento de Valores</div>
        <table class="valores-table">
          <tr class="principal-row">
            <td><strong>Serviço Principal</strong>${servico.tipo_servico_nome ? ` <span style="color:#64748b;font-size:12px;">(${escapeHtml(servico.tipo_servico_nome)})</span>` : ''}</td>
            <td class="right">${fmtMoney(servico.valor_principal)}</td>
          </tr>
          ${itensRows}
          <tr class="total-row">
            <td>TOTAL GERAL</td>
            <td class="right">${fmtMoney(servico.valor_total)}</td>
          </tr>
        </table>
      </div>

      <!-- VALOR DO RECIBO -->
      <div class="recibo-amount">
        <div class="label-rec">Valor recebido neste recibo</div>
        <div class="amount">${fmtMoney(r.valor_recibo)}</div>
        <div class="context">${escapeHtml(r.contexto || '')} · Data do pagamento: ${fmtDate((r.data_pagamento || '').slice(0,10))}</div>
      </div>

      <!-- DECLARAÇÃO -->
      <div class="declaration">
        Recebi(emos) de <strong>${escapeHtml(cliente.nome || '—')}</strong>${cliente.cpf ? ` (CPF/CNPJ ${escapeHtml(cliente.cpf)})` : ''} a quantia de
        <strong>${fmtMoney(r.valor_recibo)}</strong> referente aos serviços acima prestados${r.parcela ? ` (parcela ${r.parcela.numero})` : ''}.
        Para clareza firmo o presente recibo.
      </div>

      <!-- ASSINATURA -->
      <div class="signature">
        <div class="signature-line"></div>
        <div class="signature-name">${escapeHtml(nomeRecebedor)}</div>
        <div class="signature-role">${escapeHtml(empresa.nome || '')}</div>
      </div>
    </div>

    <!-- FOOTER -->
    <div class="footer">
      Recibo gerado em ${new Date(r.gerado_em).toLocaleString('pt-BR')} por ${escapeHtml(r.gerado_por || '—')} · ID #${(servico.id || '').slice(0,8)}
    </div>
  </div>

  <script>
    setTimeout(() => window.print(), 500);
  </script>
</body>
</html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
  } catch (e) {
    toast.error('Erro ao gerar recibo');
    console.error(e);
  }
}

function escapeHtml(str) {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
